import { VariantProps } from 'class-variance-authority';
import { useRef } from 'react';
import { CircleQuestionIcon } from '../../Icons';
import { viewItemFieldVariants } from './logics';
import styles from './styles.module.scss';

/**
 * ラベルに添えるヘルプの内容・表示制御ハンドラーをまとめたオブジェクト。
 *
 * ViewItemField はヘルプの表示手段（ツールチップ等）を知らず、呼び出し側から
 * 渡された showHelp / hideHelp を呼ぶだけに徹する（Table/HeadCell の HelpContent と同型）。
 */
type HelpContent = {
  /** ヘルプの本文 */
  content: React.ReactNode;
  /** ヘルプを表示するハンドラー（アンカー要素と本文を渡す） */
  showHelp: (anchorEl: HTMLElement, content: React.ReactNode) => void;
  /** ヘルプを非表示にするハンドラー */
  hideHelp: () => void;
  /** ヘルプの id（aria-describedby に使用） */
  helpId?: string;
};

type Props = {
  label: string;
  value: string;
  direction?: VariantProps<typeof viewItemFieldVariants>['direction'];
  /** ラベルに添えるヘルプ。指定時、label の隣にヘルプアイコンを描画する */
  helpContent?: HelpContent;
};

/**
 * 詳細画面で使用する項目名と値を表示するコンポーネント
 *
 * @param label - ラベル
 * @param value - 値
 * @param direction - レイアウト方向（column または row、デフォルトは column）
 * @param helpContent - ラベルに添えるヘルプ
 * @returns
 */
export const ViewItemField: React.FC<Props> = ({ label, value, direction, helpContent }) => {
  const helpIconRef = useRef<HTMLSpanElement>(null);

  const showHelp = () => {
    if (!helpContent || !helpIconRef.current) return;
    helpContent.showHelp(helpIconRef.current, helpContent.content);
  };

  const hideHelp = () => {
    helpContent?.hideHelp();
  };

  return (
    <div className={viewItemFieldVariants({ direction })}>
      <span
        className={[styles.label, helpContent ? styles.labelWithHelp : undefined]
          .filter(Boolean)
          .join(' ')}
        aria-describedby={helpContent?.helpId}
      >
        {label}
        {helpContent && (
          <span
            ref={helpIconRef}
            className={styles.helpIcon}
            onMouseEnter={showHelp}
            onMouseLeave={hideHelp}
            aria-hidden
          >
            <CircleQuestionIcon size='xs' />
          </span>
        )}
      </span>
      <span className={styles.value}>{value}</span>
    </div>
  );
};

type ViewItemFieldWithChildrenProps = Omit<Props, 'value'> & {
  children: React.ReactNode;
};

/**
 * 詳細画面で使用する項目名と任意の子要素を表示するコンポーネント
 *
 * value が文字列以外（バッジなど）の場合に使用する ViewItemField の派生コンポーネント。
 *
 * @param label - ラベル
 * @param direction - レイアウト方向（column または row、デフォルトは column）
 * @param children - 値の代わりに表示する子要素
 */
export const ViewItemFieldWithChildren: React.FC<ViewItemFieldWithChildrenProps> = ({
  label,
  direction,
  children,
}) => (
  <div className={viewItemFieldVariants({ direction })}>
    <span className={styles.label}>{label}</span>
    <div className={styles.value}>{children}</div>
  </div>
);
